import React, { useCallback, useMemo, useState } from 'react';
import { set, get, cloneDeep } from 'lodash';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useProductCostingState } from '../ProductCostingContext';
import { checkIsNumber, checkCurrency, calculateTotValues, roundOff, restrictToTwoDecimals } from '../../../../Components/helper';
import { additionalComponentFieldPath, errorpath } from '../fieldPath';
import useShowHideSectionHooks from '../Hooks/useShowHideSectionHooks';
import useValidationHooks from '../Hooks/useValidationHooks';
import { useComponent } from '../../../UserSetting/UsersLibrary/UserRights';
import BombiixAGGrid from '../../../../Components/BombiixAGGrid';
import type { BombiixColumnDefOrGroup, BombiixFillBatchItem, BombiixRowId } from '../../../../Components/BombiixAGGrid/types';
import CustomTooltip from '../../../../Components/Tooltip/CustomTooltip';
import CostingCardShell from './CostingCardShell';
import { SIDE_MAX_ROWS, gridHeight } from './costingRowHeights';
import type { AdditionalComponentState, AdditionalRow } from '../types';

const BLANK_ADDITIONAL_COMPONENT_ROW: AdditionalRow = {
  colourway: [],
  quantityUom: null,
  totalQuantityUom: null,
  quantityPerColSizLen: '',
  pricePerUoM: '',
  title: '',
  isPricePerUnitDisabled: false,
  isTotalPriceDisabled: false,
};

/** A `{ name, _id }` option as stored on a row once picked from a dropdown. */
interface AdditionalOptionRef {
  _id?: string;
  name?: string;
}

/** The validation messages `globalError.additionalComponent` carries, one entry per row. */
interface AdditionalComponentRowError {
  colourway?: string;
  title?: string;
  quantityPerColSizLen?: string;
  quantityUom?: string;
  pricePerUoM?: string;
  totalPrice?: string;
}

/** The redux slices this table reads. */
interface AdditionalComponentRootState {
  newProductCostingData: { productCostingPreviousData?: Record<string, unknown> };
  Users: { logInRights?: unknown };
  UserDetails: { isSupplierLoggedIn?: boolean };
}

/**
 * One rendered row. `type`, not `interface`, so the row keeps the implicit index signature
 * BombiixAGGrid's generic requires; intersecting with `AdditionalRow` narrows the fields the
 * columns read, since `unknown & T` is `T`.
 */
type AdditionalComponentGridRow = AdditionalRow & {
  __cagId: string;
  __err: AdditionalComponentRowError;
  colourway?: AdditionalOptionRef;
  quantityUom?: AdditionalOptionRef;
  isPricePerUnitDisabled?: boolean;
  isTotalPriceDisabled?: boolean;
  isNewEntryBySupplier?: boolean;
  isChangedBySupplierColourway?: boolean;
  isChangedBySupplierTitle?: boolean;
  isChangedBySupplierTotalQuantityPerSizColLen?: boolean;
  isChangedBySupplierQuantityUom?: boolean;
  isChangedBySupplierPricePerUnit?: boolean;
  isChangedBySupplierTotalPrice?: boolean;
};

/**
 * Additional Component card. Exclude lives in the grid's row-actions column, which the grid only draws
 * when a row handler is passed, so a closed costing still passes them and hides + and delete through
 * `canAddRow` / `canDeleteRow` to keep Exclude visible, read-only.
 * @returns The Additional Component card.
 */
export function AdditionalComponentTable(): React.JSX.Element {
  const { t } = useTranslation('common');
  const [isFullView, setIsFullView] = useState(false);

  const { productCostingstate, globalError, CoastingAddEditRights, initialOptionsState, setAdditionalComponentState, additionalComponentState } =
    useProductCostingState();
  const {
    productInfo: { currency },
    isGlobalUrl,
    productTemplate,
  } = productCostingstate;
  const {
    additionalComponent: { additionalComponentInfo, sumOfTotPrice },
  } = additionalComponentState;
  const { uomOptionsArr, colourwayOptionsArr } = initialOptionsState;
  const {
    newProductCostingData: { productCostingPreviousData },
    Users: { logInRights },
    UserDetails: { isSupplierLoggedIn },
  } = useSelector((state: AdditionalComponentRootState) => state);

  /* UserRights.js declares `CheckActionPermission(rights, section, module = null, action = null)`,
     so TypeScript infers the last two parameters as `null` from their defaults. The cast restores
     the signature the function actually accepts — same shim as ProductCostingContext. */
  const { CheckActionPermission } = useComponent() as {
    CheckActionPermission: (rights: unknown, section: string, module?: string | null, action?: string | null) => boolean;
  };
  const additionalComponent = globalError.additionalComponent as AdditionalComponentRowError[] | undefined;
  const { onChangeCheckIfErrorExist } = useValidationHooks();

  const { statusState, controlAmberColour, hideColourCol, onLoadDisable, isSupplierView} = useShowHideSectionHooks();

  const MasterAddEditAccess = CheckActionPermission(logInRights, 'products', 'costing', 'addEdit');

  const handleInputChange = async (value: unknown, path: string, supplierPath: string[] | undefined, index: number, columnKeyName?: string): Promise<void> => {
    const state: AdditionalComponentState = cloneDeep(additionalComponentState);
    set(state, path, value);
    if (supplierPath) {
      if (isSupplierLoggedIn || isGlobalUrl) {
        const oldData: Record<string, unknown> | undefined = cloneDeep(productCostingPreviousData);
        const previousValue = get(oldData, path);
        if (previousValue !== value) {
          supplierPath.forEach((elePath) => {
            set(state, elePath, true);
          });
        } else {
          supplierPath.forEach((elePath) => {
            set(state, elePath, false);
          });
        }
      }
    }
    await calculateTotPrice(state, columnKeyName, index);
  };

  const calculateTotPrice = async (copyState: AdditionalComponentState, columnKeyName?: string, index?: number): Promise<void> => {
    const originalData = copyState?.additionalComponent?.additionalComponentInfo || [];
    const updatedArray: AdditionalRow[] = [];
    if (originalData?.length > 0) {
      for (const [i, data] of originalData.entries()) {
        const quantityPerColSizLen = Number(data?.quantityPerColSizLen || 0);
        const pricePerUoM = Number(data?.pricePerUoM || 0);
        const totalPrice = Number(data?.totalPrice || 0);

        const updated: AdditionalRow = { ...data };

        switch (columnKeyName) {
          case 'quantityPerColSizLen':
            if ((quantityPerColSizLen >= 0 && pricePerUoM >= 0 && totalPrice >= 0) || (quantityPerColSizLen >= 0 && pricePerUoM >= 0)) {
              updated.totalPrice = quantityPerColSizLen * pricePerUoM;
            } else if (quantityPerColSizLen >= 0 && totalPrice >= 0) {
              updated.pricePerUoM = totalPrice / quantityPerColSizLen;
            }
            break;

          case 'pricePerUoM':
            /* `(index ?? -1)` only satisfies the type: the original relies on `undefined >= 0`
               being false, which is exactly what -1 gives. */
            if ((index ?? -1) >= 0 && index === i) {
              updated.isTotalPriceDisabled = true;
              updated.isPricePerUnitDisabled = false;
            }

            if (quantityPerColSizLen >= 0 && pricePerUoM >= 0) {
              updated.totalPrice = quantityPerColSizLen * pricePerUoM;
            }
            break;
          case 'totalPrice':
            if ((index ?? -1) >= 0 && index === i) {
              updated.isTotalPriceDisabled = false;
              updated.isPricePerUnitDisabled = true;
            }

            if (quantityPerColSizLen >= 0 && totalPrice >= 0) {
              updated.pricePerUoM = totalPrice / quantityPerColSizLen;
            }
            break;
          default:
            break;
        }

        updatedArray.push(updated);
      }
    }
    const datas = await calculateTotValues(updatedArray, [{ key: 'totalPrice', updateKey: 'sumOfTotPrice' }]);

    set(copyState, additionalComponentFieldPath().SUM_TOTAL_PRICE, datas.sumOfTotPrice);
    set(copyState, additionalComponentFieldPath().SELECTED_TEMP_ADDTIONAL_DATA, updatedArray);
    setAdditionalComponentState((state) => ({ ...state, ...copyState }));
  };

  const addNewRow = (): void => {
    const copyState: AdditionalComponentState = cloneDeep(additionalComponentState);
    const obj: AdditionalRow = {
      colourway: [],
      quantityUom: null,
      totalQuantityUom: null,
      quantityPerColSizLen: '',
      pricePerUoM: '',
      title: '',
      isPricePerUnitDisabled: false,
      isTotalPriceDisabled: false,
    };
    if (isSupplierLoggedIn || isGlobalUrl) {
      obj.isNewEntryBySupplier = true;
    }
    copyState?.additionalComponent?.additionalComponentInfo?.push(obj);
    setAdditionalComponentState((prevState) => ({ ...prevState, ...copyState }));
  };

  const removeRow = async (index: number): Promise<void> => {
    const copyState = additionalComponentState;
    if (Array.isArray(copyState?.additionalComponent?.additionalComponentInfo) && copyState?.additionalComponent?.additionalComponentInfo.length > 1) {
      copyState.additionalComponent.additionalComponentInfo.splice(index, 1);
      await calculateTotPrice(copyState);
    }
  };
  const updateError = (path: string): void => onChangeCheckIfErrorExist(path, globalError);

  const sourceRows = Array.isArray(additionalComponentInfo) && additionalComponentInfo.length > 0 ? additionalComponentInfo : [BLANK_ADDITIONAL_COMPONENT_ROW];

  const rowData = useMemo<AdditionalComponentGridRow[]>(() => {
    if (onLoadDisable) return [];
    return sourceRows.map((row, i) => ({
      ...(row as AdditionalComponentGridRow),
      __cagId: (row?._id as string | undefined) || `idx_${i}`,
      __err: {
        colourway: get(additionalComponent, `[${i}].colourway`, ''),
        title: get(additionalComponent, `[${i}].title`, ''),
        quantityPerColSizLen: get(additionalComponent, `[${i}].quantityPerColSizLen`, ''),
        quantityUom: get(additionalComponent, `[${i}].quantityUom`, ''),
        pricePerUoM: get(additionalComponent, `[${i}].pricePerUoM`, ''),
        totalPrice: get(additionalComponent, `[${i}].totalPrice`, ''),
      },
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onLoadDisable, additionalComponentInfo, additionalComponent, sumOfTotPrice, t]);

  const indexFromRowId = useCallback((rowId: BombiixRowId) => rowData.findIndex((row) => row.__cagId === rowId), [rowData]);

  const amberClass = (flag: boolean | undefined): string => (flag && controlAmberColour ? 'suggested-data' : '');

  const onRowChange = useCallback(
    (rowId: BombiixRowId, field: string, value: unknown) => {
      const index = indexFromRowId(rowId);
      if (index < 0) return;

      switch (field) {
        case 'colourway':
          handleInputChange(
            value,
            additionalComponentFieldPath(index).SELECTED_COLOURWAY,
            [
              additionalComponentFieldPath(index).IS_CHANGED_BY_SUPPLIER_TOTAL_QUANTITY,
              additionalComponentFieldPath(index).IS_CHANGED_BY_SUPPLIER_TOTAL_PRICE,
              additionalComponentFieldPath(index).IS_CHANGED_BY_SUPPLIER_COLOURWAY,
            ],
            index,
          );
          if (get(additionalComponent, `[${index}].colourway`, '')) updateError(errorpath(index).ADDITIONAL_COMPONENT.ERROR_COLOURWAY);
          break;

        case 'title':
          handleInputChange(
            value,
            additionalComponentFieldPath(index).ITEM,
            [additionalComponentFieldPath(index).IS_CHANGED_BY_SUPPLIER_TITLE],
            index,
            'title',
          );
          if (get(additionalComponent, `[${index}].title`, '')) updateError(errorpath(index).ADDITIONAL_COMPONENT.ERROR_TITLE);
          break;

        case 'quantityPerColSizLen': {
          const restricted = restrictToTwoDecimals(value);
          if (!checkIsNumber(restricted || 0)) return;
          handleInputChange(
            restricted,
            additionalComponentFieldPath(index).QUANTITY_PER_UNIT,
            [
              additionalComponentFieldPath(index).IS_CHANGED_BY_SUPPLIER_QUANTITY_PER_SIZE_COl,
              additionalComponentFieldPath(index).IS_CHANGED_BY_SUPPLIER_TOTAL_QUANTITY,
              additionalComponentFieldPath(index).IS_CHANGED_BY_SUPPLIER_TOTAL_PRICE,
            ],
            index,
            'quantityPerColSizLen',
          );
          if (get(additionalComponent, `[${index}].quantityPerColSizLen`, '')) updateError(errorpath(index).ADDITIONAL_COMPONENT.ERROR_QUANTITY);
          break;
        }

        case 'quantityUom':
          handleInputChange(
            value,
            additionalComponentFieldPath(index).UOM_ONE_SELECTED,
            [additionalComponentFieldPath(index).IS_CHANGED_BY_SUPPLIER_QUANTITY_UOM],
            index,
          );
          if (get(additionalComponent, `[${index}].quantityUom`, '')) updateError(errorpath(index).ADDITIONAL_COMPONENT.ERROR_QUANTITY_UOM);
          break;

        case 'pricePerUoM': {
          const restricted = restrictToTwoDecimals(value);
          if (!checkIsNumber(restricted || 0)) return;
          handleInputChange(
            restricted,
            additionalComponentFieldPath(index).PRICE_PER_UNIT,
            [additionalComponentFieldPath(index).IS_CHANGED_BY_SUPPLIER_TOTAL_PRICE, additionalComponentFieldPath(index).IS_CHANGED_BY_SUPPLIER_PRICE_PER_UNIT],
            index,
            'pricePerUoM',
          );
          if (get(additionalComponent, `[${index}].pricePerUoM`, '')) updateError(errorpath(index).ADDITIONAL_COMPONENT.ERROR_PRICEPER_UNIT);
          break;
        }

        case 'totalPrice': {
          const restricted = restrictToTwoDecimals(value);
          if (!checkIsNumber(restricted || 0)) return;
          handleInputChange(
            restricted,
            additionalComponentFieldPath(index).TOTAL_PRICE,
            [additionalComponentFieldPath(index).IS_CHANGED_BY_SUPPLIER_TOTAL_PRICE, additionalComponentFieldPath(index).IS_CHANGED_BY_SUPPLIER_PRICE_PER_UNIT],
            index,
            'totalPrice',
          );
          if (get(additionalComponent, `[${index}].pricePerUoM`, '')) updateError(errorpath(index).ADDITIONAL_COMPONENT.ERROR_PRICEPER_UNIT);
          break;
        }

        case 'isExcluded':
          handleInputChange(value, additionalComponentFieldPath(index).IS_EXCLUDED, undefined, index);
          break;

        default:
          break;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [indexFromRowId, additionalComponent],
  );

  const handleFillCells = async (changes: BombiixFillBatchItem<AdditionalComponentGridRow>[]): Promise<void> => {
    if (!Array.isArray(changes) || changes.length === 0) return;
    const state: AdditionalComponentState = cloneDeep(additionalComponentState);
    const applySupplier = isSupplierLoggedIn || isGlobalUrl;
    const oldData: Record<string, unknown> | null = applySupplier ? cloneDeep(productCostingPreviousData) : null;
    let columnKeyName: string | undefined;

    const setSupplierFlags = (paths: string[] | undefined, valuePath: string, value: unknown): void => {
      if (!applySupplier || !paths) return;
      const changed = get(oldData, valuePath) !== value;
      paths.forEach((elePath) => set(state, elePath, changed));
    };

    changes.forEach((change) => {
      const index = indexFromRowId(change.rowId);
      if (index < 0) return;
      const fp = additionalComponentFieldPath(index);
      let value = change.newValue;

      switch (change.field) {
        case 'colourway':
          set(state, fp.SELECTED_COLOURWAY, value);
          setSupplierFlags(
            [fp.IS_CHANGED_BY_SUPPLIER_TOTAL_QUANTITY, fp.IS_CHANGED_BY_SUPPLIER_TOTAL_PRICE, fp.IS_CHANGED_BY_SUPPLIER_COLOURWAY],
            fp.SELECTED_COLOURWAY,
            value,
          );
          break;
        case 'title':
          set(state, fp.ITEM, value);
          setSupplierFlags([fp.IS_CHANGED_BY_SUPPLIER_TITLE], fp.ITEM, value);
          columnKeyName = 'title';
          break;
        case 'quantityUom':
          set(state, fp.UOM_ONE_SELECTED, value);
          setSupplierFlags([fp.IS_CHANGED_BY_SUPPLIER_QUANTITY_UOM], fp.UOM_ONE_SELECTED, value);
          break;
        case 'quantityPerColSizLen': {
          const restricted = restrictToTwoDecimals(value);
          if (!checkIsNumber(restricted || 0)) return;
          value = restricted;
          set(state, fp.QUANTITY_PER_UNIT, value);
          setSupplierFlags(
            [fp.IS_CHANGED_BY_SUPPLIER_QUANTITY_PER_SIZE_COl, fp.IS_CHANGED_BY_SUPPLIER_TOTAL_QUANTITY, fp.IS_CHANGED_BY_SUPPLIER_TOTAL_PRICE],
            fp.QUANTITY_PER_UNIT,
            value,
          );
          columnKeyName = 'quantityPerColSizLen';
          break;
        }
        case 'pricePerUoM': {
          const restricted = restrictToTwoDecimals(value);
          if (!checkIsNumber(restricted || 0)) return;
          value = restricted;
          set(state, fp.PRICE_PER_UNIT, value);
          setSupplierFlags([fp.IS_CHANGED_BY_SUPPLIER_TOTAL_PRICE, fp.IS_CHANGED_BY_SUPPLIER_PRICE_PER_UNIT], fp.PRICE_PER_UNIT, value);
          set(state, `additionalComponent.additionalComponentInfo[${index}].isTotalPriceDisabled`, true);
          set(state, `additionalComponent.additionalComponentInfo[${index}].isPricePerUnitDisabled`, false);
          columnKeyName = 'pricePerUoM';
          break;
        }
        case 'totalPrice': {
          const restricted = restrictToTwoDecimals(value);
          if (!checkIsNumber(restricted || 0)) return;
          value = restricted;
          set(state, fp.TOTAL_PRICE, value);
          setSupplierFlags([fp.IS_CHANGED_BY_SUPPLIER_TOTAL_PRICE, fp.IS_CHANGED_BY_SUPPLIER_PRICE_PER_UNIT], fp.TOTAL_PRICE, value);
          set(state, `additionalComponent.additionalComponentInfo[${index}].isTotalPriceDisabled`, false);
          set(state, `additionalComponent.additionalComponentInfo[${index}].isPricePerUnitDisabled`, true);
          columnKeyName = 'totalPrice';
          break;
        }
        case 'isExcluded':
          set(state, fp.IS_EXCLUDED, value);
          break;
        default:
          break;
      }
    });

    await calculateTotPrice(state, columnKeyName, -1);
  };

  const showActions = !statusState && MasterAddEditAccess;

  const columnDefs = useMemo<BombiixColumnDefOrGroup<AdditionalComponentGridRow>[]>(() => {
    /* In full-page view the columns reflow proportionally instead of staying pinned at their
       compact widths, so the card uses the width it has just been given. `flex: null` restores the
       fixed width when the card collapses — omitting it would leave the grid flexed. */
    const sideColSizing = isFullView ? { flex: 1, minWidth: 100 } : { width: 120, minWidth: 120, flex: null };
    const quantityColSizing = isFullView ? { flex: 1, minWidth: 100 } : { width: 100, minWidth: 100, flex: null };
    const itemColSizing = isFullView ? { flex: 2, minWidth: 240 } : { width: 120, minWidth: 120, flex: 1 };
    const cols: BombiixColumnDefOrGroup<AdditionalComponentGridRow>[] = [];

    if (hideColourCol) {
      cols.push({
        field: 'colourway',
        headerName: t('DEVLOPMENT_ORDER.EXPAND_VIEW.ADDITIONAL_TABLE.COLOURWAY'),
        required: true,
        cellType: 'select',
        ...sideColSizing,
        sortable: false,
        getCellReadOnly: () => statusState,
        getCellDisabled: () => !CoastingAddEditRights,
        getCellError: (row: AdditionalComponentGridRow) => row.__err.colourway,
        getCellClass: (row: AdditionalComponentGridRow) => amberClass(row.isChangedBySupplierColourway || row.isNewEntryBySupplier),
        cellProps: {
          options: colourwayOptionsArr,
          getOptionLabel: (opt: AdditionalOptionRef) => opt?.name,
          getOptionValue: (opt: AdditionalOptionRef) => opt?._id,
          accessor: (row: AdditionalComponentGridRow) => row?.colourway?.name,
          fallback: '',
        },
      });
    }

    cols.push({
      field: 'title',
      headerName: t('DEVLOPMENT_ORDER.EXPAND_VIEW.ADDITIONAL_TABLE.ITEM'),
      cellType: 'text',
      ...itemColSizing,
      sortable: false,
      getCellReadOnly: () => statusState,
      getCellDisabled: () => !CoastingAddEditRights,
      getCellError: (row: AdditionalComponentGridRow) => row.__err.title,
      getCellClass: (row: AdditionalComponentGridRow) => amberClass(row.isChangedBySupplierTitle || row.isNewEntryBySupplier),
      cellProps: { fallback: '' },
    });

    cols.push({
      field: 'quantityPerColSizLen',
      headerName: t('DEVLOPMENT_ORDER.EXPAND_VIEW.ADDITIONAL_TABLE.QUANTITY'),
      required: true,
      cellType: 'text',
      ...quantityColSizing,
      sortable: false,
      getCellReadOnly: () => statusState,
      getCellDisabled: () => !CoastingAddEditRights,
      getCellError: (row: AdditionalComponentGridRow) => row.__err.quantityPerColSizLen,
      getCellClass: (row: AdditionalComponentGridRow) => amberClass(row.isChangedBySupplierTotalQuantityPerSizColLen || row.isNewEntryBySupplier),
      cellProps: { type: 'number', fallback: '' },
    });

    cols.push({
      field: 'quantityUom',
      headerName: t('DEVLOPMENT_ORDER.EXPAND_VIEW.ADDITIONAL_TABLE.UOM'),
      required: true,
      cellType: 'select',
      ...sideColSizing,
      sortable: false,
      getCellReadOnly: () => statusState || !CoastingAddEditRights,
      getCellError: (row: AdditionalComponentGridRow) => row.__err.quantityUom,
      getCellClass: (row: AdditionalComponentGridRow) => amberClass(row.isChangedBySupplierQuantityUom || row.isNewEntryBySupplier),
      cellProps: {
        options: uomOptionsArr,
        getOptionLabel: (opt: AdditionalOptionRef) => opt?.name,
        getOptionValue: (opt: AdditionalOptionRef) => opt?.name,
        accessor: (row: AdditionalComponentGridRow) => row?.quantityUom?.name,
        fallback: '',
      },
    });

    cols.push({
      field: 'pricePerUoM',
      headerName: t('costing:DEVLOPMENT_ORDER.EXPAND_VIEW.ADDITIONAL_TABLE.PRICE'),
      required: true,
      cellType: 'currency',
      ...sideColSizing,
      sortable: false,
      getCellReadOnly: () => statusState || !CoastingAddEditRights,
      getCellDisabled: (row: AdditionalComponentGridRow) => Boolean(row.isPricePerUnitDisabled),
      getCellError: (row: AdditionalComponentGridRow) => row.__err.pricePerUoM,
      getCellClass: (row: AdditionalComponentGridRow) => amberClass(row.isChangedBySupplierPricePerUnit || row.isNewEntryBySupplier),
      cellProps: { accessor: (row: AdditionalComponentGridRow) => row?.pricePerUoM, fallback: '' },
    });

    cols.push({
      field: 'totalPrice',
      headerName: t('DEVLOPMENT_ORDER.EXPAND_VIEW.ADDITIONAL_TABLE.PRICE_PER_UNIT'),
      required: true,
      cellType: 'currency',
      ...sideColSizing,
      sortable: false,
      getCellReadOnly: () => statusState || !CoastingAddEditRights,
      getCellDisabled: (row: AdditionalComponentGridRow) => Boolean(row.isTotalPriceDisabled),
      getCellError: (row: AdditionalComponentGridRow) => row.__err.totalPrice,
      getCellClass: (row: AdditionalComponentGridRow) => amberClass(row.isChangedBySupplierTotalPrice || row.isNewEntryBySupplier),
      cellProps: {
        accessor: (row: AdditionalComponentGridRow) => {
          const formattedTotal = `${checkCurrency(currency, row?.totalPrice)} ${roundOff(row?.totalPrice, 2, true)}`;
          return row?.__cagTotal ? `${t('costing:DEVLOPMENT_ORDER.EXPAND_VIEW.ADDITIONAL_TABLE.TOTAL')}: ${formattedTotal}` : formattedTotal;
        },
      },
    });

    return cols;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    hideColourCol,
    statusState,
    CoastingAddEditRights,
    controlAmberColour,
    colourwayOptionsArr,
    uomOptionsArr,
    currency,
    isSupplierView,
    MasterAddEditAccess,
    isFullView,
    t,
  ]);

  /* `exactOptionalPropertyTypes` rejects an explicit `undefined` on these optional props, so the
     original's `showActions ? … : undefined` becomes a conditional spread. Same result. */
  const rowActionProps = showActions || !isSupplierView ? { onAddRow: addNewRow, onDeleteRow: (rowId: BombiixRowId) => removeRow(indexFromRowId(rowId)) } : {};

  const excludeActionProps = !isSupplierView
    ? {
        renderRowActionExtras: ({ data, rowReadOnly }: { data?: AdditionalComponentGridRow | undefined; rowReadOnly: boolean }) =>
          data?.__cagTotal ? null : (
            <CustomTooltip tooltipText={t('DEVLOPMENT_ORDER.TESTING_TABLE.EXCLUDE')}>
              <label className="cag-checkbox">
                <input
                  type="checkbox"
                  className="cag-checkbox-input"
                  checked={Boolean(data?.isExcluded)}
                  disabled={statusState || rowReadOnly || !CoastingAddEditRights}
                  onChange={(e) => onRowChange(data?.__cagId as BombiixRowId, 'isExcluded', e.target.checked)}
                  aria-label={t('DEVLOPMENT_ORDER.TESTING_TABLE.EXCLUDE')}
                />
              </label>
            </CustomTooltip>
          ),
        rowActionsWidth: 102,
      }
    : {};

  return (
    <CostingCardShell
      onFullChange={setIsFullView}
      title={(productTemplate?.additionalComponentTitle as string | undefined) || t('DEVLOPMENT_ORDER.COSTING_DETAILS.NEW_ADDITIONAL_TABLE')}
    >
      {(fullView) => (
        <>
          <div className="costing-grid-fill" style={fullView ? undefined : { minHeight: gridHeight(Math.min(rowData.length, SIDE_MAX_ROWS)) }}>
            <BombiixAGGrid<AdditionalComponentGridRow>
              fullWidthHorizontalScroll
              gridOptions={{ suppressMovableColumns: true, rowHeight: 40 }}
              suppressColumnResize
              hideCellErrorMessage
              revealHeaderControlsOnHover
              sortable={false}
              showColumnMenu={false}
              enableFillHandle
              onFillCells={handleFillCells}
              columnDefs={columnDefs}
              rowData={rowData}
              getRowId={(row) => row.__cagId}
              onRowChange={onRowChange}
              {...rowActionProps}
              {...excludeActionProps}
              canAddRow={() => showActions}
              canDeleteRow={(row) => showActions && !row?.__cagTotal && additionalComponentInfo?.length > 1}
              isRowReadOnly={(row) => Boolean(row?.__cagTotal)}
              getRowClass={(row) => (row?.__cagTotal ? 'cag-hier-row--total' : '')}
              currencySymbol={checkCurrency(currency, undefined, true)}
              height="100%"
            />
          </div>
          {onLoadDisable && <p className="text-center mt-2 mb-0">{t('COSTING_TO_DO_LIST.NO_RECORD_FOUND')}</p>}
        </>
      )}
    </CostingCardShell>
  );
}

export default AdditionalComponentTable;
